import { css } from "@assets/style/util/cssUtils"
import CountBadge, { CountBadgeProps } from "@components/square-count-badge/CountBadge"
import NotificationBadge, {
  NotificationBadgeProps,
} from "@components/square-count-badge/NotificationBadge"
import {
  DiscoIconKinds,
  DiscoLink,
  DiscoLinkProps,
  DiscoTextButton,
  DiscoTooltip,
} from "@disco-ui"
import { darken, useTheme } from "@material-ui/core"
import classNames from "classnames"
import { forwardRef, ReactNode } from "react"
import classes from "./DiscoTab.module.css"

export interface DiscoTabProps
  extends Pick<DiscoLinkProps, "children">,
    Pick<
      React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
      "onMouseOver" | "onMouseLeave"
    > {
  testid: string
  to?: DiscoLinkProps["to"]
  externalLink?: string
  onClick?: () => void
  active?: boolean
  className?: string
  variant?: "default" | "blue-background" | "grey-track"
  size?: "small" | "default"
  notificationConfig?: NotificationBadgeProps["config"]
  rightContent?: ReactNode
  leftIcon?: React.ReactElement | DiscoIconKinds
  notifications?: number
  count?: number
  disabled?: boolean
  activeColor?: string
  countBadgeProps?: Partial<CountBadgeProps>
  tooltip?: ReactNode
}

const DiscoTab = forwardRef<HTMLDivElement, DiscoTabProps>(
  (
    {
      testid,
      children,
      to,
      externalLink,
      onClick,
      active = false,
      className,
      notificationConfig,
      variant = "default",
      size = "default",
      rightContent,
      leftIcon,
      notifications,
      count,
      disabled,
      activeColor,
      countBadgeProps,
      tooltip,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme()

    const style = css({
      "--disco-tab-active-color": activeColor,
    })

    return (
      <DiscoTooltip content={tooltip} disabled={!tooltip} placement={"top"}>
        <div
          ref={ref}
          data-testid={testid}
          className={classNames(classes.tabContainer, className)}
          data-active={active}
          data-variant={variant}
          data-size={size}
          style={style}
          {...rest}
        >
          {renderContent()}
          {rightContent}
        </div>
      </DiscoTooltip>
    )

    function renderContent() {
      if (to || externalLink) {
        return (
          <DiscoLink
            className={classes.text}
            to={to || externalLink}
            target={externalLink ? "_blank" : undefined}
          >
            {children}
            {renderCountBadge()}
          </DiscoLink>
        )
      }

      if (typeof children === "string") {
        return (
          <DiscoTextButton
            disabled={disabled}
            className={classes.text}
            leftIcon={leftIcon}
            iconSize={variant === "grey-track" ? 24 : 20}
            onClick={onClick}
            variant={variant === "grey-track" ? "primary" : "currentColor"}
          >
            {children}
            {renderCountBadge()}
          </DiscoTextButton>
        )
      }

      return children
    }

    function renderCountBadge() {
      if (notificationConfig)
        return (
          <NotificationBadge
            className={classes.countBadge}
            testid={testid}
            config={notificationConfig}
            aria-label={"Unread notifications"}
            {...countBadgeProps}
          />
        )

      if (notifications)
        return (
          <CountBadge
            className={classes.countBadge}
            testid={testid}
            count={notifications}
            {...countBadgeProps}
          />
        )
      return (
        count !== undefined && (
          <CountBadge
            className={classes.countBadge}
            testid={testid}
            count={count}
            maxCount={-1}
            size={20}
            color={active ? "var(--groovy-primary-main)" : "var(--groovy-text-primary)"}
            backgroundColor={
              // TO DO: fully remove references to theme.palette and use of darken
              active
                ? theme.palette.type === "dark"
                  ? darken(theme.palette.primary.dark, 0.5)
                  : "var(--groovy-primary-light)"
                : "var(--groovy-neutral-100)"
            }
            {...countBadgeProps}
          />
        )
      )
    }
  }
)

export default DiscoTab
